// src/pages/AdminReportsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  doc,
  getDoc
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useOrgId } from "../utils/tenant";

function isoToday() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function monthStartIso() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-01`;
}

function toDate(iso) {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function fmtLocal(dt) {
  try {
    return dt ? dt.toLocaleString() : "—";
  } catch {
    return "—";
  }
}

function safeStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function isUrl(s) {
  return typeof s === "string" && /^https?:\/\//i.test(s);
}


function cssColorToRgb(color) {
  const c = String(color || "").trim();
  if (!c) return [47, 168, 182]; // fallback accent
  if (c.startsWith("#")) {
    let hex = c.slice(1);
    if (hex.length === 3) hex = hex.split("").map(ch => ch + ch).join("");
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0,2), 16);
      const g = parseInt(hex.slice(2,4), 16);
      const b = parseInt(hex.slice(4,6), 16);
      if ([r,g,b].every(n => Number.isFinite(n))) return [r,g,b];
    }
    return [47, 168, 182];
  }
  const m = c.match(/rgba?\((\s*\d+)[,\s]+(\s*\d+)[,\s]+(\s*\d+)/i);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return [47, 168, 182];
}

async function urlToDataUri(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, {
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      headers: { Accept: "image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8" },
    });
    if (!res.ok) throw new Error("Failed to load image: " + res.status);
    const blob = await res.blob();

    // jsPDF is most reliable with JPEG/PNG. Convert any fetched image blob to PNG
    // first so production Firebase Storage URLs are embedded as actual photos, not
    // printed as long download links. This needs Storage CORS to allow matrixos.co.za.
    try {
      const objectUrl = URL.createObjectURL(blob);
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width || 1;
            canvas.height = img.naturalHeight || img.height || 1;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
          } catch (err) {
            reject(err);
          } finally {
            URL.revokeObjectURL(objectUrl);
          }
        };
        img.onerror = (err) => {
          URL.revokeObjectURL(objectUrl);
          reject(err);
        };
        img.src = objectUrl;
      });
      return dataUrl;
    } catch (_) {
      // Fallback to raw data URI if canvas conversion is not available.
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
  } catch (e) {
    console.warn("urlToDataUri failed", url, e);
    return null;
  }
}

function dataUriType(uri) {
  if (!uri || typeof uri !== "string") return "PNG";
  if (uri.startsWith("data:image/jpeg") || uri.startsWith("data:image/jpg")) return "JPEG";
  if (uri.startsWith("data:image/webp")) return "WEBP";
  return "PNG";
}

function subheadingSaveMeta(subheadingSaves, subheading, fallbackIdx = 0) {
  if (!subheadingSaves || typeof subheadingSaves !== "object") return null;

  const clean = String(subheading || "").trim();
  const key = clean ? `h_${clean}` : `h_blank_${fallbackIdx}`;

  return subheadingSaves[key] || null;
}

function flattenValues(values) {
  /**
   * Normalize many possible "values" shapes into a flat list of:
   * { subheading: string, field: string, value: any }
   *
   * Supports:
   * 1) { fieldLabel: value }
   * 2) { Subheading: { fieldLabel: value, ... }, ... }
   * 3) Array of items (legacy / different Neo payloads)
   * 4) Object with numeric keys "0","1","2"... (array-like)
   */
  const out = [];
  if (values === null || values === undefined) return out;

  // Case: array of entries
  if (Array.isArray(values)) {
    values.forEach((item, idx) => {
      if (item && typeof item === "object") {
        const subheading =
          item.subheading || item.section || item.group || item.area || "";
        const field =
          item.field || item.label || item.name || item.title || `Item ${idx + 1}`;

        // best-effort value extraction
        let value =
          item.value ??
          item.answer ??
          item.val ??
          item.url ??
          item.urls ??
          item.photoUrls ??
          item.photos ??
          item;

        out.push({ subheading: String(subheading || ""), field: String(field || ""), value });
      } else {
        out.push({ subheading: "", field: `Item ${idx + 1}`, value: item });
      }
    });
    return out;
  }

  // Case: plain object
  if (typeof values !== "object") return out;

  const entries = Object.entries(values);

  // Case: array-like object with numeric keys
  const numericKeys = entries.every(([k]) => /^\d+$/.test(k));
  if (numericKeys) {
    entries
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .forEach(([k, v], idx) => {
        if (v && typeof v === "object") {
          const subheading =
            v.subheading || v.section || v.group || v.area || "";
          const field =
            v.field || v.label || v.name || v.title || `Item ${Number(k) + 1 || idx + 1}`;
          let value =
            v.value ??
            v.answer ??
            v.val ??
            v.url ??
            v.urls ??
            v.photoUrls ??
            v.photos ??
            v;
          out.push({ subheading: String(subheading || ""), field: String(field || ""), value });
        } else {
          out.push({ subheading: "", field: `Item ${Number(k) + 1 || idx + 1}`, value: v });
        }
      });
    return out;
  }

  // Determine if it looks grouped by subheading: any value is a non-array object
  const looksGrouped = entries.some(([_, v]) => {
    if (!v || typeof v !== "object") return false;
    if (Array.isArray(v)) return false;
    return true;
  });

  if (!looksGrouped) {
    for (const [field, value] of entries) {
      out.push({ subheading: "", field, value });
    }
    return out;
  }

  for (const [subheading, group] of entries) {
    if (!group || typeof group !== "object" || Array.isArray(group)) {
      out.push({ subheading: "", field: String(subheading), value: group });
      continue;
    }
    for (const [field, value] of Object.entries(group)) {
      out.push({ subheading: String(subheading), field, value });
    }
  }
  return out;
}

function buildCsv(rows, storesById, includeStore) {
  const lines = [];
  const header = [
    ...(includeStore ? ["Store"] : []),
    "Submitted At",
    "By",
    "Subheading",
    "Field",
    "Value",
  ];
  lines.push(header.map((h) => `"${h.replace(/"/g, '""')}"`).join(","));

  for (const r of rows) {
    const flat = flattenValues(r.values);
    if (flat.length === 0) {
      const line = [
        ...(includeStore ? [(storesById[r.storeId]?.name || storesById[r.storeId]?.storeName || storesById[r.storeId]?.label || r.storeId)] : []),
        fmtLocal(r.at),
        r.staff || "",
        "",
        "",
        "",
      ];
      lines.push(line.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","));
      continue;
    }

    for (const item of flat) {
      const v = item.value;
      let valStr = "";

      const meta = subheadingSaveMeta(r.subheadingSaves, item.subheading || "", 0);

      if (Array.isArray(v)) {
        const urls = v.map((x)=> (x && typeof x==='object' ? (x.url||x.downloadURL||x.downloadUrl||x.href) : x)).filter((x)=>isUrl(x));
        valStr = (urls.length ? urls : v.map((x)=>safeStr(x))).join(" | ");
      } else if (v && typeof v === "object") {
        valStr = safeStr(v);
      } else {
        valStr = safeStr(v);
      }

      const line = [
        ...(includeStore ? [(storesById[r.storeId]?.name || storesById[r.storeId]?.storeName || storesById[r.storeId]?.label || r.storeId)] : []),
        fmtLocal(r.at),
        r.staff || "",
        item.subheading || "",
        item.field || "",
        meta?.savedBy ? `Saved by ${meta.savedBy}${meta?.savedAt ? ` at ${fmtLocal(meta.savedAt?.toDate ? meta.savedAt.toDate() : meta.savedAt?.seconds ? new Date(meta.savedAt.seconds * 1000) : new Date(meta.savedAt))}` : ""} | ${valStr}` : valStr,
      ];
      lines.push(line.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","));
    }
  }

  return lines.join("\n");
}

function buildExcelHtml(selectedRows, storesById, includeStore, activeTab) {
  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const headCols = `
    ${includeStore ? "<th>Store</th>" : ""}
    <th>Submitted At</th>
    <th>By</th>
    <th>Subheading</th>
    <th>Field</th>
    <th>Value</th>
  `;

  const bodyRows = selectedRows
    .map((r) => {
      const flat = flattenValues(r.values);
      const rows = flat.length
        ? flat
        : [{ subheading: "", field: "", value: "" }];

      return rows
        .map((item) => {
         let valStr = "";
          const v = item.value;
          const meta = subheadingSaveMeta(r.subheadingSaves, item.subheading || "", 0);
          if (Array.isArray(v)) {
        const urls = v.map((x)=> (x && typeof x==='object' ? (x.url||x.downloadURL||x.downloadUrl||x.href) : x)).filter((x)=>isUrl(x));
        valStr = (urls.length ? urls : v.map((x)=>safeStr(x))).join(" | ");
      }
          else valStr = safeStr(v);

                    const finalVal = meta?.savedBy
            ? `Saved by ${meta.savedBy}${meta?.savedAt ? ` at ${fmtLocal(meta.savedAt?.toDate ? meta.savedAt.toDate() : meta.savedAt?.seconds ? new Date(meta.savedAt.seconds * 1000) : new Date(meta.savedAt))}` : ""} | ${valStr}`
            : valStr;

          return `<tr>
            ${includeStore ? `<td>${esc((storesById[r.storeId]?.name || storesById[r.storeId]?.storeName || storesById[r.storeId]?.label || r.storeId))}</td>` : ""}
            <td>${esc(fmtLocal(r.at))}</td>
            <td>${esc(r.staff || "")}</td>
            <td>${esc(item.subheading || "")}</td>
            <td>${esc(item.field || "")}</td>
            <td>${esc(finalVal)}</td>
          </tr>`;
        })
        .join("");
    })
    .join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(activeTab)} - Export</title>
  <style>
    body { font-family: Arial, sans-serif; }
    h2 { margin: 0 0 12px; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ddd; padding: 8px; vertical-align: top; }
    th { background: #f3f3f3; }
  </style>
</head>
<body>
  <h2>${esc(activeTab)} — Export</h2>
  <table>
    <thead><tr>${headCols}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;
}

// eslint-disable-next-line no-unused-vars
function buildPrintHtml(selectedRows, storesById, includeStore, activeTab, orgName, activeSectionObj) {
  const esc = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const storeLabel = (id) => {
    const s = storesById?.[id] || {};
    return (
      s.displayName ||
      s.name ||
      s.storeName ||
      s.label ||
      s.title ||
      s.displayName ||
      s.branch ||
      s.store ||
      s.storeTitle ||
      s.store_label ||
      id
    );
  };

  const yesNo = (v) => {
    if (v === true) return "Yes";
    if (v === false) return "No";
    if (typeof v === "string") {
      const t = v.trim().toLowerCase();
      if (t === "true") return "Yes";
      if (t === "false") return "No";
    }
    return v;
  };

  const getValueAt = (values, idx) => {
    if (!values) return undefined;
    if (Array.isArray(values)) return values[idx];
    if (typeof values === "object") return values[idx] ?? values[String(idx)];
    return undefined;
  };

  const buildGroupsFromFields = (fields) => {
    const groups = [];
    let current = null;
    (fields || []).forEach((f, idx) => {
      const sub = String((f?.subheading || "")).trim();
      if (!current || current.sub !== sub) {
        current = { sub, items: [] };
        groups.push(current);
      }
      current.items.push({ idx, label: f?.label || "" });
    });
    return groups;
  };

  const renderValue = (v) => {
    v = yesNo(v);

    // Photos: either array of url/objects, or single object with url/downloadURL, etc.
    const asImgUrl = (x) => {
      if (!x) return "";
      if (typeof x === "string") return x;
      if (typeof x === "object")
        return x.url || x.downloadURL || x.downloadUrl || x.src || "";
      return "";
    };

    if (Array.isArray(v)) {
      const urls = v.map(asImgUrl).filter(Boolean);
      if (urls.length) {
        return `<div class="img">${urls
          .map((u) => `<img src="${esc(u)}" alt="photo" />`)
          .join("")}</div>`;
      }
      return esc(v.join(", "));
    }

    if (v && typeof v === "object") {
      const u = asImgUrl(v);
      if (u) return `<div class="img"><img src="${esc(u)}" alt="photo" /></div>`;
      try {
        return esc(JSON.stringify(v));
      } catch {
        return esc(String(v));
      }
    }

    return esc(v ?? "");
  };

  const fields = activeSectionObj?.fields || [];
  const groups = buildGroupsFromFields(fields);

  const cards = selectedRows
    .map((r) => {
      // NOTE: our rows use { values } directly; keep fallbacks for older shapes
      const values = r?.values ?? r?.data?.values ?? r?.data?.answers ?? r?.data?.data ?? r?.data ?? {};
      const submittedAt = r?.at ? new Date(r.at) : null;
      const flat = flattenValues(values);
      const byField = new Map();
      const bySubField = new Map();
      flat.forEach((it) => {
        const f = String(it.field || "").trim();
        const s = String(it.subheading || "").trim();
        if (f) byField.set(f, it.value);
        if (s && f) bySubField.set(`${s}||${f}`, it.value);
      });


      const groupHtml = groups
        .map((g) => {
          const subTitle = g.sub || "General";
          const rows = g.items
            .map((it) => {
              let v =
                bySubField.get(`${String(g.sub || "").trim()}||${String(it.label || "").trim()}`) ??
                byField.get(String(it.label || "").trim());
              if (v === undefined) v = getValueAt(values, it.idx);
              return `<tr><td class="field">${esc(it.label || "")}</td><td>${renderValue(v)}</td></tr>`;
            })
            .join("");
          return `
            <div class="sub">
              <div class="subh">${esc(subTitle)}</div>
              <table>
                <thead><tr><th>Field</th><th>Value</th></tr></thead>
                <tbody>${rows}</tbody>
              </table>
            </div>
          `;
        })
        .join("");

      const headBits = [
        includeStore ? `<span><b>Store:</b> ${esc(storeLabel(r.storeId))}</span>` : "",
        `<span><b>Submitted:</b> ${esc(submittedAt ? submittedAt.toLocaleString() : "—")}</span>`,
        `<span><b>By:</b> ${esc(r.staff || "")}</span>`,
      ].filter(Boolean).join(" &nbsp; ");

      return `
        <div class="card">
          <div class="meta">${headBits}</div>
          ${groupHtml}
        </div>
      `;
    })
    .join("");

  const titleOrg = orgName ? esc(orgName) : "Reports";

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${titleOrg} - ${esc(activeTab)} (Export)</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 18px; }
    h1 { margin: 0 0 6px; font-size: 18px; }
    h2 { margin: 0 0 14px; font-size: 14px; color: #333; font-weight: 600; }
    .card { border: 1px solid #ddd; border-radius: 10px; padding: 12px; margin-bottom: 14px; }
    .meta { margin-bottom: 10px; font-size: 12px; color: #333; }
    .sub { margin-top: 10px; }
    .subh { font-weight: 700; margin: 8px 0; background: var(--accent, #2fa8b6); color: #fff; padding: 8px 10px; border-radius: 8px; }
    table { border-collapse: collapse; width: 100%; font-size: 12px; }
    th, td { border: 1px solid #ddd; padding: 8px; vertical-align: top; }
    th { background: #f3f3f3; }
    td.field { width: 40%; }
    .img { display: flex; gap: 10px; flex-wrap: wrap; }
    .img img { height: 120px; border-radius: 8px; border: 1px solid #ddd; }
    @media print { @page { size: A4 portrait; margin: 10mm; } }
  </style>
</head>
<body>
  <h1>${titleOrg}</h1>
  <h2>${esc(activeTab)}</h2>
  ${cards}
</body>
</html>`;
}

/**
 * Admin cash-up style reports.
 * Key rule: reports MUST reflect the Staff Dashboard Builder.
 * - If a section is deleted and has submissions, it should be archived (not shown by default) but preserved.
 * - If a section has no submissions, deletion can be a hard delete.
 */
export default function AdminReportsPage({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();
  const [orgName, setOrgName] = useState("");

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("ALL");

  const [sections, setSections] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [from, setFrom] = useState(monthStartIso());
  const [to, setTo] = useState(isoToday());
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // New: expand + export selector
  const [expandedId, setExpandedId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportKind, setExportKind] = useState(null); // "pdf" | "csv" | "excel"
  const [exporting, setExporting] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const storesById = useMemo(() => {
    const map = {};
    for (const s of stores) map[s.id] = s;
    return map;
  }, [stores]);

  const storeLabelForId = (id) => {
    const s = storesById?.[id];
    return (
      s?.displayName ||
      s?.name ||
      s?.storeName ||
      s?.label ||
      s?.title ||
      s?.displayName ||
      s?.branch ||
      s?.store ||
      s?.storeTitle ||
      s?.store_label ||
      id
    );
  };


  
// Load stores (prefer cashup_stores names; filter archived)
useEffect(() => {
  if (!orgId) return;
  let alive = true;

  const deriveDisplayName = (data, fallbackId) => {
    if (!data || typeof data !== "object") return fallbackId || "";
    const direct =
      data.displayName ||
      data.name ||
      data.storeName ||
      data.store ||
      data.label ||
      data.title ||
      data.branchName ||
      data.branch ||
      data.storeTitle ||
      data.store_label;

    if (direct) return String(direct);

    const nested =
      data.profile?.displayName ||
      data.profile?.name ||
      data.profile?.storeName ||
      data.profile?.branch ||
      data.meta?.displayName ||
      data.meta?.name ||
      data.details?.name ||
      data.details?.storeName;

    return String(nested || fallbackId || "");
  };

  const isArchivedStore = (data) => {
    if (!data || typeof data !== "object") return false;
    const status = String(data.status || "").toLowerCase();
    return (
      data.archived === true ||
      data.isArchived === true ||
      status === "archived" ||
      data.active === false
    );
  };

  (async () => {
    try {
      const byId = new Map();

      const upsert = (id, data, prefer = false) => {
        if (!id) return;
        const displayName = deriveDisplayName(data, id);
        const archived = isArchivedStore(data);

        const prev = byId.get(id);
        if (!prev) {
          byId.set(id, { id, ...data, displayName, __archived: archived });
          return;
        }

        // Merge, but prefer a "better" name if:
        // - caller says prefer=true (cashup_stores), OR
        // - previous name looked like an id and new one is not
        const prevName = String(prev.displayName || prev.name || id);
        const prevLooksId = prevName === id;
        const nextLooksBetter = displayName && displayName !== id;

        const next = {
          ...prev,
          ...data,
          __archived: prev.__archived || archived,
        };

        if (prefer || (prevLooksId && nextLooksBetter)) {
          next.displayName = displayName;
          if (!next.name && (data?.name || data?.storeName)) next.name = data.name || data.storeName;
        }

        byId.set(id, next);
      };

      // legacy (org scoped) – usually has the store name
      try {
        const snap = await getDocs(collection(db, "orgs", orgId, "cashup_stores"));
        snap.docs.forEach((d) => upsert(d.id, d.data() || {}, true));
      } catch {}

      // New store metadata. A scoped Management user must not enumerate parent
      // store documents outside their assigned sites; cashup_stores already gives
      // us the selector catalogue and the remaining reads use explicit site IDs.
      if (siteScope.allSites) {
        try {
          const snap = await getDocs(collection(db, "orgs", orgId, "stores"));
          snap.docs.forEach((d) => upsert(d.id, d.data() || {}, false));
        } catch {}
      }

      // extra legacy: root-level stores with orgId field (some builds used this)
      try {
        const snap = await getDocs(query(collection(db, "stores"), where("orgId", "==", orgId)));
        snap.docs.forEach((d) => upsert(d.id, d.data() || {}, false));
      } catch {}

      let list = Array.from(byId.values());
      if (!siteScope.allSites) {
        const allowedSites = new Set(siteScope.siteIds || []);
        list = list.filter((s) => allowedSites.has(s.id));
      }

      // default: hide archived stores (there is a "Show archived" checkbox for results, not for dropdown)
      list = list.filter((s) => !s.__archived);

      list.sort((a, b) =>
        String(a.displayName || a.name || a.id).localeCompare(String(b.displayName || b.name || b.id))
      );

      if (!alive) return;
      setStores(list);
    } catch (e) {
      if (!alive) return;
      setErr(e?.message || "Failed to load stores");
    }
  })();

  return () => {
    alive = false;
  };
}, [orgId, siteScope.allSites, siteScope.siteIds]);

// Load org name (for PDF heading)
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "orgs", orgId));
        const d = snap.exists() ? (snap.data() || {}) : {};
        const name =
          d.displayName ||
          d.orgName ||
          d.name ||
          d.title ||
          d.brandName ||
          "";
        if (alive) setOrgName(String(name || ""));
      } catch {
        // ignore
      }
    })();
    return () => {
      alive = false;
    };
  }, [orgId, siteScope.allSites, siteScope.siteIds]);


  // Load sections for selected store
  useEffect(() => {
    if (!orgId) return;
    let alive = true;

    (async () => {
      setErr("");
      try {
        // sections are stored in orgs/{orgId}/staff_dashboard
        const qs = storeId === "ALL"
          ? query(collection(db, "orgs", orgId, "staff_dashboard"))
          : query(collection(db, "orgs", orgId, "staff_dashboard"), where("storeId", "==", storeId));

        const snap = await getDocs(qs);
        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((s) => !!s.name)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || String(a.name).localeCompare(String(b.name)));

        if (!alive) return;

        const hasCustom = list.some((s) => String(s.name || "").trim().toLowerCase() === "custom report");
        const nextList = hasCustom
          ? list
          : [
              ...list,
              {
                id: "custom_report_fixed",
                name: "Custom Report",
                order: 99999,
                fixed: true,
                fields: [],
              },
            ];

        setSections(nextList);

        // choose active tab if needed
        const visible = nextList.filter((s) => showArchived || !(s.isArchived || s.archived));
        if (!visible.find((s) => s.name === activeTab)) {
          setActiveTab(visible[0]?.name || "");
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load sections");
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, storeId, showArchived]);

  const tabs = useMemo(() => {
    const list = (sections || []).filter((s) => showArchived || !(s.isArchived || s.archived));
    // de-dupe by name (builder can be per-store); take first occurrence for tab purposes.
    const seen = new Set();
    return list.filter((s) => {
      const k = String(s.name);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [sections, showArchived]);

  async function loadReports() {
    if (!orgId) return;
    setLoading(true);
    setErr("");

    try {
      if (!activeTab) {
        setRows([]);
        setLoading(false);
        return;
      }

      const fromD = toDate(from);
      const toD = toDate(to);
      if (!fromD || !toD) throw new Error("Invalid date range");

      // inclusive end-of-day
      const toEnd = new Date(toD);
      toEnd.setHours(23, 59, 59, 999);

      const storeIds = storeId === "ALL" ? stores.map((s) => s.id) : [storeId];

      const out = [];

      // Query per-store submissions. This is the canonical location.
      for (const sid of storeIds) {
        if (!sid) continue;
        const isCustomReportTab = String(activeTab || "").trim().toLowerCase() === "custom report";
        const qy = query(
          collection(db, "orgs", orgId, "stores", sid, "submissions"),
          where("submittedAt", ">=", fromD),
          where("submittedAt", "<=", toEnd),
          orderBy("submittedAt", "desc"),
          limit(250)
        );

        try {
          const snap = await getDocs(qy);
          snap.docs.forEach((docSnap) => {
            const d = docSnap.data() || {};
            const at = d.submittedAt?.toDate ? d.submittedAt.toDate() : null;
            const sectionName = String(d.sectionName || "").trim();
            const sectionId = String(d.sectionId || "").trim();

            const matchesTab = isCustomReportTab
              ? sectionId === "custom_report_fixed" || sectionName.toLowerCase() === "custom report"
              : sectionName === activeTab;

            if (!matchesTab) return;

            out.push({
              id: `${sid}_${docSnap.id}`,
              storeId: sid,
              staff: d.submittedBy || d.staffName || "",
              at,
              values: d.values ?? d.responses ?? d.items ?? d.entries ?? d.data ?? {},
              customReport: d.customReport || null,
              subheadingSaves: d.subheadingSaves || {},
            });
          });
        } catch (e) {
          // ignore per-store query errors so one missing store doesn't break everything
        }
      }

      // Sort again across stores
      out.sort((a, b) => (b.at?.getTime?.() || 0) - (a.at?.getTime?.() || 0));

      setRows(out);
      // Reset export selections because result-set changed
      setSelectedIds(new Set());
    } catch (e) {
      setErr(e?.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }

  // initial load & reload on tab/store change
  useEffect(() => {
    if (!orgId) return;
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeTab, storeId]);


  const storeLabel = (sid) => {
    const st = storesById?.[sid];
    return (
      st?.name ||
      st?.storeName ||
      st?.label ||
      st?.title ||
      st?.store ||
      st?.displayName ||
      sid
    );
  };

  const activeSectionObj = useMemo(() => {
    return (sections || []).find((sec) => String(sec.name) === String(activeTab)) || null;
  }, [sections, activeTab]);

  const getValueAt = (values, idx) => {
    if (values == null) return undefined;
    if (Array.isArray(values)) return values[idx];
    if (typeof values === "object") return values[idx] ?? values[String(idx)];
    return undefined;
  };

  const buildGroupsFromFields = (fields) => {
    const groups = [];
    let current = null;

    (fields || []).forEach((f, idx) => {
      const sub = String((f?.subheading || "")).trim();
      if (!current || current.sub !== sub) {
        current = { sub, items: [] };
        groups.push(current);
      }
      current.items.push({ idx, label: f?.label || "", type: f?.type || "" });
    });

    return groups;
  };

  const renderSubmissionFromSection = (row) => {
    if (row?.customReport) {
      const blocks = Array.isArray(row.customReport.items) ? row.customReport.items : [];
      const title = String(row.customReport.title || "Custom Report");
      return (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-white/10">
              <tr>
                <th className="px-4 py-3 w-[40%]">Field</th>
                <th className="px-4 py-3">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-white/10">
                <td colSpan={2} className="px-4 py-2 text-white font-semibold" style={{ backgroundColor: "var(--accent, rgba(47,168,182,0.9))" }}>
                  {title}
                </td>
              </tr>

              {blocks.map((item, idx) => {
                if (item?.type === "subheading") {
                  return (
                    <tr key={item.id || idx} className="border-t border-white/10 bg-white/5">
                      <td colSpan={2} className="px-4 py-2 text-white font-semibold">
                        {item.value || "Subheading"}
                      </td>
                    </tr>
                  );
                }

                let valueForCell = item?.value;
                if (item?.type === "check") {
                  valueForCell = item?.value === true ? "Yes" : item?.value === false ? "No" : "—";
                }

                return (
                  <tr key={item.id || idx} className="border-t border-white/10">
                    <td className="px-4 py-3 text-white/85 align-top">
                      {item?.label || "Field"}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {renderValueCell(valueForCell)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    const sec = activeSectionObj;
    const fields = sec?.fields || [];
    if (!fields.length) return (
      <div className="text-white/60 text-sm">No fields configured for this report in Staff Dashboard Builder.</div>
    );

    const groups = buildGroupsFromFields(fields);

    return (
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-white/10">
            <tr>
              <th className="px-4 py-3 w-[40%]">Field</th>
              <th className="px-4 py-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((grp, gi) => (
              <React.Fragment key={gi}>
                <tr className="border-t border-white/10">
                  <td colSpan={2} className="px-4 py-2 text-white font-semibold" style={{ backgroundColor: "var(--accent, rgba(47,168,182,0.9))" }}>
                    {grp.sub || activeTab}
                  </td>
                </tr>
                <tr className="border-t border-white/10 bg-white/5">
                  <td colSpan={2} className="px-4 py-2 text-white/70 text-xs">
                    {(() => {
                      const meta = subheadingSaveMeta(row.subheadingSaves, grp.sub, grp.items?.[0]?.idx || 0);
                      return meta?.savedBy ? (
                        <>
                          Saved by <span className="text-white font-semibold">{meta.savedBy}</span>
                          {meta?.savedAt ? ` at ${fmtLocal(meta.savedAt?.toDate ? meta.savedAt.toDate() : meta.savedAt?.seconds ? new Date(meta.savedAt.seconds * 1000) : new Date(meta.savedAt))}` : ""}
                        </>
                      ) : (
                        <em>No saved-by info</em>
                      );
                    })()}
                  </td>
                </tr>

                {grp.items.map((it) => {
                  const v = getValueAt(row.values, it.idx);
                  if (v === undefined || v === null || v === "") return null;

                  // Checkbox normalization
                  const isCheckbox = it.type === "checkbox" || it.type === "tick" || it.type === "toggle";
                  const valueForCell = isCheckbox ? !!v : v;

                  return (
                    <tr key={it.idx} className="border-t border-white/10">
                      <td className="px-4 py-3 text-white/85 align-top">
                        {it.label || "—"}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {renderValueCell(valueForCell)}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  function renderValueCell(value) {
    // Booleans
    if (typeof value === "boolean") {
      return <div className="text-white/90">{value ? "Yes" : "No"}</div>;
    }

    // Objects that wrap urls/photos (common in Neo uploads)
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const maybeUrl = value.url || value.downloadURL || value.href;
      if (isUrl(maybeUrl)) {
        return (
          <a
            href={maybeUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-black/20 p-2 min-h-[140px] min-w-[140px] max-w-[360px]"
            title="Open photo"
          >
            <img src={maybeUrl} alt="Upload" className="max-w-[360px] max-h-[260px] w-auto h-auto object-contain" loading="lazy" />
          </a>
        );
      }

      const maybeUrls = value.urls || value.photoUrls || value.photos;
      if (Array.isArray(maybeUrls)) {
        return renderValueCell(maybeUrls);
      }
    }

    // Photos (array of urls)

    if (Array.isArray(value)) {
      const urls = value
        .map((x) => {
          if (isUrl(x)) return x;
          if (x && typeof x === "object") {
            const u = x.url || x.downloadURL || x.href;
            if (isUrl(u)) return u;
          }
          return null;
        })
        .filter(Boolean);
      if (urls.length) {
        return (
          <div className="flex flex-wrap gap-3 items-start">
            {urls.map((u, idx) => (
              <a
                key={idx}
                href={u}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-black/20 p-2 min-h-[140px] min-w-[140px] max-w-[360px]"
                title="Open photo"
              >
                <img
                  src={u}
                  alt="Upload"
                  className="max-w-full max-h-[260px] w-auto h-auto object-contain mx-auto"
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        );
      }
      return <div className="text-white/80">{value.map((x) => safeStr(x)).join(" • ")}</div>;
    }

    // Single url photo
    if (isUrl(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-black/20 p-2 min-h-[140px] min-w-[140px] max-w-[360px]"
          title="Open photo"
        >
          <img src={value} alt="Upload" className="max-w-[360px] max-h-[260px] w-auto h-auto object-contain" loading="lazy" />
        </a>
      );
    }

    // Object
    if (value && typeof value === "object") {
      return (
        <pre className="text-white/80 whitespace-pre-wrap break-words m-0">
          {safeStr(value)}
        </pre>
      );
    }

    // Primitive
    return <div className="text-white/90">{safeStr(value) || "—"}</div>;
  }

  function openExport(kind) {
    setPdfPreviewUrl(null);
    setExportKind(kind);
    setExportOpen(true);

    // If user hasn't selected anything, default-select all
    setSelectedIds((prev) => {
      if (prev && prev.size > 0) return new Set(prev);
      return new Set(rows.map((r) => r.id));
    });
  }

  function closeExport() {
    setPdfPreviewUrl(null);
    setExportOpen(false);
    setExportKind(null);
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll(flag) {
    setSelectedIds(() => {
      if (!flag) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }

  
  async function doExport() {
    if (exporting) return; // prevent double-click
    const includeStore = storeId === "ALL";
    const selected = rows.filter((r) => selectedIds.has(r.id));
    if (!selected.length) return;

    const baseName = `${activeTab || "Reports"}_${isoToday()}`.replace(/[^\w-]+/g, "_");

    if (exportKind === "csv") {
      const csv = buildCsv(selected, storesById, includeStore);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      closeExport();
      return;
    }

    if (exportKind === "excel") {
      const html = buildExcelHtml(selected, storesById, includeStore, activeTab);
      const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      closeExport();
      return;
    }

    // PDF (download; user can open preview after export completes)
    try {
      setExporting(true);

      // Pull the accent (branding) color from CSS vars if available
      const cssAccent = typeof window !== "undefined"
        ? getComputedStyle(document.documentElement).getPropertyValue("--accent") ||
          getComputedStyle(document.documentElement).getPropertyValue("--brand-accent") ||
          ""
        : "";
      const accentRgb = cssColorToRgb(cssAccent);

      const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const marginX = 40;
      let y = 40;

      // Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(orgName || "Reports", marginX, y);
      y += 18;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(`${activeTab || "Reports"} (Export)`, marginX, y);
      y += 18;

      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(new Date().toLocaleString(), marginX, y);
      doc.setTextColor(0);
      y += 16;

      // Helpers to match on-screen ordering/labels (prevents "Item 1" etc)
      const yesNo = (v) => {
        if (v === true) return "Yes";
        if (v === false) return "No";
        if (typeof v === "string") {
          const t = v.trim().toLowerCase();
          if (t === "true") return "Yes";
          if (t === "false") return "No";
        }
        return v;
      };

      const getValueAt = (values, idx) => {
        if (!values) return undefined;
        if (Array.isArray(values)) return values[idx];
        if (typeof values === "object") return values[idx] ?? values[String(idx)];
        return undefined;
      };

      const buildGroupsFromFields = (fields) => {
        const groups = [];
        let current = null;
        (fields || []).forEach((f, idx) => {
          const sub = String((f?.subheading || "")).trim();
          if (!current || current.sub !== sub) {
            current = { sub, items: [] };
            groups.push(current);
          }
          current.items.push({ idx, label: f?.label || "" });
        });
        return groups;
      };

      const sectionFields = activeSectionObj?.fields || [];
      const groupsFromFields = buildGroupsFromFields(sectionFields);

      // Build each submission block
      for (let i = 0; i < selected.length; i++) {
        const r = selected[i];

        const storeLabel = includeStore
          ? (storesById[r.storeId]?.name || storesById[r.storeId]?.storeName || storesById[r.storeId]?.label || r.storeId)
          : (storesById[r.storeId]?.name || storesById[r.storeId]?.storeName || storesById[r.storeId]?.label || r.storeId);

        // Page break safety
        if (y > doc.internal.pageSize.getHeight() - 120) {
          doc.addPage();
          y = 40;
        }

        // Submission meta line
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text(storeLabel || "Store", marginX, y);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(80);
        doc.text(`Submitted: ${fmtLocal(r.at)}   By: ${r.staff || "—"}`, marginX + 140, y);
        doc.setTextColor(0);
        y += 14;

        // Use schema field order if available (matches screen + avoids Item 1)
        // Keep a fallback to flattenValues for unknown shapes.
        const values = r?.values ?? r?.data?.values ?? r?.data?.answers ?? r?.data?.data ?? r?.data ?? {};
        const flat = flattenValues(values);
        const byField = new Map();
        const bySubField = new Map();
        flat.forEach((it) => {
          const f = String(it.field || "").trim();
          const s = String(it.subheading || "").trim();
          if (f) byField.set(f, it.value);
          if (s && f) bySubField.set(`${s}||${f}`, it.value);
        });

        const groupsToRender = groupsFromFields.length
          ? groupsFromFields
          : (() => {
              const m = new Map();
              for (const it of flat) {
                const k = String((it.subheading || "").trim() || "General");
                if (!m.has(k)) m.set(k, []);
                m.get(k).push({ idx: null, label: it.field || "" });
              }
              return Array.from(m.entries()).map(([sub, items]) => ({ sub, items }));
            })();

          for (const g of groupsToRender) {
          const sub = String(g.sub || "General");
          const subMeta = subheadingSaveMeta(r.subheadingSaves, sub, g.items?.[0]?.idx || 0);
          if (y > doc.internal.pageSize.getHeight() - 140) {
            doc.addPage();
            y = 40;
          }

          // Subheading bar (branding accent)
          const barH = 18;
          doc.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
          doc.roundedRect(marginX, y, pageW - marginX * 2, barH, 6, 6, "F");
          doc.setTextColor(255, 255, 255);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text(sub === " " ? " " : sub, marginX + 10, y + 12);
          doc.setTextColor(0);
          y += barH + 8;

          if (subMeta?.savedBy) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(90);
            doc.text(
              `Saved by ${subMeta.savedBy}${subMeta?.savedAt ? ` at ${fmtLocal(subMeta.savedAt?.toDate ? subMeta.savedAt.toDate() : subMeta.savedAt?.seconds ? new Date(subMeta.savedAt.seconds * 1000) : new Date(subMeta.savedAt))}` : ""}`,
              marginX + 4,
              y
            );
            doc.setTextColor(0);
            y += 10;
          }

          const body = [];
          const photoCells = [];
          // Preload photos per row (best-effort; if CORS blocks, fall back to URLs)
          for (const it of g.items || []) {
            const field = String(it.label || "").trim();
            if (!field) continue;
            let v =
              bySubField.get(`${String(g.sub || "").trim()}||${field}`) ??
              byField.get(field);
            if (v === undefined) v = getValueAt(values, it.idx);
            v = yesNo(v);

            let isPhotos = false;
            let urls = [];

            if (Array.isArray(v)) {
              urls = v
                .map((x) => (x && typeof x === "object" ? (x.url || x.downloadURL || x.downloadUrl || x.href) : x))
                .filter((x) => isUrl(x));
              const imgCount = urls.filter((u) => /\.(png|jpe?g|webp)(\?|$)/i.test(u) || /alt=media/i.test(u)).length;
              isPhotos = urls.length > 0 && imgCount >= Math.max(1, Math.floor(urls.length / 2));
            } else if (typeof v === "string" && isUrl(v)) {
              urls = [v];
              isPhotos = /\.(png|jpe?g|webp)(\?|$)/i.test(v) || /alt=media/i.test(v);
            }

            if (isPhotos && urls.length) {
              const limited = urls.slice(0, 6);
              const dataUris = (await Promise.all(limited.map(urlToDataUri))).filter(Boolean);
              if (dataUris.length) {
                body.push([field, ""]);
                const cols = 2;
                const tileH = 110;
                const rowsNeeded = Math.ceil(dataUris.length / cols);
                const desiredH = Math.max(tileH, rowsNeeded * tileH + (rowsNeeded - 1) * 8 + 12);
                photoCells.push({ rowIndex: body.length - 1, dataUris, desiredH });
              } else {
                body.push([field, limited.join(" | ")]);
              }
            } else {
              let valStr = "";
              if (Array.isArray(v)) valStr = v.map((x) => safeStr(x)).join(" | ");
              else if (v && typeof v === "object") valStr = safeStr(v);
              else valStr = safeStr(v);
              body.push([field, valStr || "—"]);
            }
          }

          autoTable(doc, {
            startY: y,
            head: [["Field", "Value"]],
            body,
            styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "top" },
            headStyles: { fillColor: [245, 245, 245], textColor: 20, fontStyle: "bold" },
            columnStyles: { 0: { cellWidth: 220 }, 1: { cellWidth: pageW - marginX * 2 - 220 } },
            didParseCell: (data) => {
              if (data.section !== "body") return;
              if (data.column.index !== 1) return;
              const found = photoCells.find((p) => p.rowIndex === data.row.index);
              if (!found) return;
              data.cell.styles.minCellHeight = Math.max(
                data.cell.styles.minCellHeight || 0,
                found.desiredH || 110
              );
            },
            didDrawCell: (data) => {
              // Draw larger photo tiles in Value column (similar size to Reports UI)
              if (data.section !== "body") return;
              if (data.column.index !== 1) return;

              const found = photoCells.find((p) => p.rowIndex === data.row.index);
              if (!found) return;

              const gap = 8;
              const pad = 6;
              const maxW = data.cell.width - pad * 2;
              const maxH = data.cell.height - pad * 2;

              const cols = 2;
              const tileW = (maxW - gap * (cols - 1)) / cols;
              const tileH = Math.min(110, maxH);

              let y0 = data.cell.y + pad;
              let col = 0;

              for (const uri of found.dataUris) {
                try {
                  const t = dataUriType(uri);
                  doc.addImage(
                    uri,
                    t,
                    data.cell.x + pad + col * (tileW + gap),
                    y0,
                    tileW,
                    tileH
                  );
                } catch {
                  // ignore
                }

                col++;
                if (col >= cols) {
                  col = 0;
                  y0 += tileH + gap;
                  if (y0 > data.cell.y + pad + maxH - tileH) break;
                }
              }
            },
          });

          y = doc.lastAutoTable.finalY + 14;
        }

        // Divider between submissions
        if (i !== selected.length - 1) {
          doc.setDrawColor(220);
          doc.line(marginX, y, pageW - marginX, y);
          y += 12;
        }
      }

      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);

      // Auto-download
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Save preview URL so user can open it after export completes
      setPdfPreviewUrl(url);

      // Revoke later (give user time to open preview)
      setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);

      // Keep modal open so the user can click "Open preview"
    } catch (e) {
      console.error(e);
      setErr("PDF export failed. If photos are blank, Storage CORS may be blocking image downloads.");
    } finally {
      setExporting(false);
    }
  }

  if (!orgId) return <div className="p-6 text-white/70">Loading…</div>;

  const reportsMobileCss = `
    .neo-admin-reports-page {
      max-width: 100%;
      overflow-x: hidden;
      padding-bottom: calc(1.5rem + env(safe-area-inset-bottom, 0px));
    }
    .neo-admin-reports-page * {
      box-sizing: border-box;
    }
    .neo-admin-report-tabs {
      max-width: 100%;
      overflow-x: auto;
      overflow-y: hidden;
      flex-wrap: nowrap !important;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: thin;
      padding-bottom: 0.25rem;
    }
    .neo-admin-report-tabs > button {
      flex: 0 0 auto;
      white-space: nowrap;
    }
    .neo-admin-report-filters select,
    .neo-admin-report-filters input {
      min-height: 42px;
      max-width: 100%;
    }
    .neo-report-submission-scroll {
      width: 100%;
      max-width: 100%;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      border-radius: 0.9rem;
    }
    .neo-report-submission-scroll table {
      min-width: 680px;
    }
    .neo-report-submission-scroll img {
      max-width: min(100%, 360px) !important;
      height: auto !important;
    }
    .neo-admin-export-modal {
      max-height: min(88vh, 760px);
      display: flex;
      flex-direction: column;
    }
    .neo-admin-export-body {
      overflow: auto;
      -webkit-overflow-scrolling: touch;
    }
    @media (max-width: 760px) {
      .neo-admin-reports-page {
        padding: 1rem !important;
        gap: 0.9rem;
      }
      .neo-admin-reports-header {
        align-items: stretch !important;
      }
      .neo-admin-reports-header h2 {
        font-size: 1.35rem !important;
      }
      .neo-admin-reports-actions {
        width: 100%;
        gap: 0.75rem !important;
      }
      .neo-admin-reports-export-buttons {
        display: grid !important;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        width: 100%;
      }
      .neo-admin-reports-export-buttons button {
        width: 100%;
        min-height: 42px;
      }
      .neo-admin-report-filters {
        display: grid !important;
        grid-template-columns: 1fr;
        align-items: stretch !important;
        padding: 1rem !important;
        border-radius: 1rem !important;
      }
      .neo-admin-report-filters > div,
      .neo-admin-report-filters select,
      .neo-admin-report-filters input,
      .neo-admin-report-filters button {
        width: 100% !important;
      }
      .neo-admin-report-filters button {
        min-height: 44px;
      }
      .neo-admin-submission-card > button {
        align-items: flex-start !important;
        flex-direction: column;
        text-align: left;
      }
      .neo-admin-submission-card .neo-admin-submission-meta {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        line-height: 1.35;
      }
      .neo-admin-submission-card .neo-admin-submission-dot {
        display: none;
      }
      .neo-admin-submission-card .neo-admin-submission-toggle {
        align-self: flex-start;
      }
      .neo-report-submission-scroll {
        margin-inline: -0.25rem;
        padding-bottom: 0.25rem;
      }
      .neo-report-submission-scroll table {
        min-width: 720px;
        font-size: 0.82rem;
      }
      .neo-report-submission-scroll th,
      .neo-report-submission-scroll td {
        padding: 0.65rem 0.75rem !important;
      }
      .neo-admin-export-shell {
        align-items: center !important;
        justify-content: center !important;
        padding: 0.75rem !important;
      }
      .neo-admin-export-modal {
        width: min(100%, calc(100vw - 1.5rem)) !important;
        max-height: calc(100dvh - 1.5rem);
        border-radius: 1.1rem !important;
      }
      .neo-admin-export-header {
        gap: 0.8rem;
        align-items: flex-start !important;
      }
      .neo-admin-export-body {
        padding: 1rem !important;
      }
      .neo-admin-export-footer {
        display: grid !important;
        grid-template-columns: 1fr;
        width: 100%;
      }
      .neo-admin-export-footer button {
        width: 100%;
        min-height: 42px;
      }
    }
  `;

  return (
    <div className="neo-admin-reports-page p-6 text-white space-y-4">
      <style>{reportsMobileCss}</style>
      <div className="neo-admin-reports-header flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-2xl font-semibold">Reports</h2>

        <div className="neo-admin-reports-actions flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>

          {/* Export buttons */}
          <div className="neo-admin-reports-export-buttons flex gap-2">
            <button
              onClick={() => openExport("pdf")}
              className="px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
              disabled={!rows.length || loading}
              title={!rows.length ? "No submissions to export" : "Export as PDF"}
            >
              PDF
            </button>
            <button
              onClick={() => openExport("excel")}
              className="px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
              disabled={!rows.length || loading}
              title={!rows.length ? "No submissions to export" : "Export as Excel"}
            >
              Excel
            </button>
            <button
              onClick={() => openExport("csv")}
              className="px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
              disabled={!rows.length || loading}
              title={!rows.length ? "No submissions to export" : "Export as CSV"}
            >
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Tabs from Builder */}
      <div className="neo-admin-report-tabs flex gap-2 flex-wrap">
        {tabs.length === 0 ? (
          <div className="text-white/60">No report sections created yet.</div>
        ) : (
          tabs.map((s) => (
            <button
              key={s.name}
              onClick={() => setActiveTab(s.name)}
              className={
                "px-3 py-2 rounded border border-white/10 " +
                (activeTab === s.name ? "bg-white/15" : "bg-white/5 hover:bg-white/10")
              }
            >
              {s.name}
            </button>
          ))
        )}
      </div>

      {/* Filters */}
      <div className="neo-admin-report-filters rounded border border-white/10 bg-white/5 p-4 flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xs text-white/70 mb-1">Store</div>
          <select
            className="bg-black/40 border border-white/10 rounded px-3 py-2"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
          >
            <option value="ALL">All stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {storeLabelForId(s.id)}
              </option>
            ))}
          </select>
        </div>

        <div
          className="cursor-pointer"
          onClick={() => {
            const el = fromRef.current;
            if (!el) return;
            if (typeof el.showPicker === "function") el.showPicker();
            else el.focus();
          }}
        >
          <div className="text-xs text-white/70 mb-1">From</div>
          <input
            ref={fromRef}
            type="date"
            className="bg-black/40 border border-white/10 rounded px-3 py-2 w-full"
            style={{ colorScheme: "dark" }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>

        <div
          className="cursor-pointer"
          onClick={() => {
            const el = toRef.current;
            if (!el) return;
            if (typeof el.showPicker === "function") el.showPicker();
            else el.focus();
          }}
        >
          <div className="text-xs text-white/70 mb-1">To</div>
          <input
            ref={toRef}
            type="date"
            className="bg-black/40 border border-white/10 rounded px-3 py-2 w-full"
            style={{ colorScheme: "dark" }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <button
          onClick={loadReports}
          className="px-4 py-2 rounded text-white"
          style={{ backgroundColor: "var(--brand-accent)" }}
        >
          Search
        </button>
      </div>

      {err && <div className="text-red-300">{err}</div>}

      {/* Submissions list (accordion / stacked) */}
      {loading ? (
        <div className="text-white/70">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-white/60">No submissions found for this section.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const isOpen = expandedId === r.id;
            const storeName = storeLabel(r.storeId);

            return (
              <div
                key={r.id}
                className="neo-admin-submission-card rounded-2xl border border-white/10 bg-white/5 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setExpandedId((cur) => (cur === r.id ? null : r.id))}
                  className="w-full px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3 hover:bg-white/5"
                >
                  <div className="neo-admin-submission-meta text-sm text-white/85">
                    {storeId === "ALL" ? (
                      <>
                        <span className="font-semibold text-white">{storeName}</span>
                        <span className="neo-admin-submission-dot text-white/60"> • </span>
                      </>
                    ) : null}
                    <span className="font-semibold text-white">{fmtLocal(r.at)}</span>
                    {r.staff ? (
                      <>
                        <span className="neo-admin-submission-dot text-white/60"> • </span>
                        <span className="text-white/80">
                          Submitted by:{" "}
                          <span className="text-white font-semibold">{r.staff}</span>
                        </span>
                      </>
                    ) : null}
                  </div>

                  <div className="neo-admin-submission-toggle text-xs px-3 py-1 rounded-full border border-white/10 bg-black/20">
                    {isOpen ? "Collapse" : "Expand"}
                  </div>
                </button>

                {isOpen ? (
                  <div className="p-4">
                    <div className="neo-report-submission-scroll">{renderSubmissionFromSection(r)}</div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}<div className="text-xs text-white/50">
        Tip: If you delete a section in the Builder and it has submissions, it will be archived and hidden here by default.
      </div>

      {/* Export selector modal */}
      {exportOpen && (
        <div className="neo-admin-export-shell fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={closeExport}
          />
          <div className="neo-admin-export-modal relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0b1220] shadow-2xl overflow-hidden">
            <div className="neo-admin-export-header px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="font-semibold">
                  Export {exportKind?.toUpperCase?.()} — Select submissions
                </div>
                <div className="text-xs text-white/60">
                  {selectedIds.size} selected
                </div>
              </div>
              <button
                onClick={closeExport}
                className="px-3 py-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-sm"
              >
                Close
              </button>
            </div>

            <div className="neo-admin-export-body p-4 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => selectAll(true)}
                  className="text-sm px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10"
                >
                  Select all
                </button>
                <button
                  onClick={() => selectAll(false)}
                  className="text-sm px-3 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10"
                >
                  Clear
                </button>
              </div>

              <div className="max-h-[55vh] overflow-auto rounded-xl border border-white/10">
                {rows.map((r) => {
                  const storeName = storeLabel(r.storeId);
                  const label = `${storeName} • ${fmtLocal(r.at)}`;

                  const checked = selectedIds.has(r.id);

                  return (
                    <label
                      key={"sel_" + r.id}
                      className="flex items-center gap-3 px-3 py-2 border-t border-white/10 first:border-t-0 hover:bg-white/5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelected(r.id)}
                      />
                      <div className="text-sm">
                        <div className="font-medium">{label}</div>
                        <div className="text-xs text-white/60">
                          {r.staff ? `By ${r.staff}` : "—"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="neo-admin-export-footer flex items-center justify-end gap-2">
                <button
                  onClick={closeExport}
                  className="px-4 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10"
                >
                  Cancel
                </button>

                {exportKind === "pdf" && pdfPreviewUrl && (
                  <button
                    onClick={() => window.open(pdfPreviewUrl, "_blank")}
                    className="px-4 py-2 rounded border border-white/10 bg-white/5 hover:bg-white/10"
                  >
                    Open preview
                  </button>
                )}
                <button
                  onClick={doExport}
                  disabled={selectedIds.size === 0 || exporting}
                  className={
                    "px-4 py-2 rounded text-white " +
                    (selectedIds.size === 0 || exporting ? "opacity-50 cursor-not-allowed" : "")
                  }
                  style={{ backgroundColor: "var(--brand-accent)" }}
                >
                  {exporting ? "Exporting…" : "Export selected"}
                </button>
              </div>

              {exportKind === "pdf" && (
                <div className="text-xs text-white/60">
                  PDF export will download the PDF. After it finishes, click "Open preview" if you want to view it in a new tab.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}